import { useMemo } from 'react';
import { IWallet } from '@efex-services/client/types';
import { WalletTypes } from '@constants/enums';
import { TAB_GLOBAL_ACCOUNT, TAB_LOAN_ACCOUNT } from '../../wallets/constants';
import { WalletTabsProps, WalletTabsResult } from './useWalletTabs.types';

const WALLET_FILTERS: Record<number, (wallet: IWallet) => boolean> = {
  [TAB_GLOBAL_ACCOUNT]: (wallet: IWallet) =>
    wallet.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT,
  [TAB_LOAN_ACCOUNT]: (wallet: IWallet) =>
    wallet.wallet.wallet_type === WalletTypes.LOAN_ACCOUNT,
};

/**
 * Custom hook to handle account tabs and data filtering
 */
export function useWalletTabs({
  walletsData = [],
  currentTab = TAB_GLOBAL_ACCOUNT,
  accountTabLabels = [],
  t,
}: WalletTabsProps): WalletTabsResult {
  const translatedTabLabels = useMemo(
    () => (t ? accountTabLabels.map((label) => t(label)) : accountTabLabels),
    [accountTabLabels, t]
  );

  const { activeTabIndices, activeTabLabels } = useMemo(() => {
    const currentActiveTabIndices: number[] = [];
    const currentTabLabels: string[] = [];

    if (walletsData.some(WALLET_FILTERS[TAB_GLOBAL_ACCOUNT])) {
      currentActiveTabIndices.push(TAB_GLOBAL_ACCOUNT);
      currentTabLabels.push(translatedTabLabels[TAB_GLOBAL_ACCOUNT]);
    }

    if (walletsData.some(WALLET_FILTERS[TAB_LOAN_ACCOUNT])) {
      currentActiveTabIndices.push(TAB_LOAN_ACCOUNT);
      currentTabLabels.push(translatedTabLabels[TAB_LOAN_ACCOUNT]);
    }

    return {
      activeTabIndices: currentActiveTabIndices,
      activeTabLabels: currentTabLabels,
    };
  }, [walletsData, translatedTabLabels]);

  const filteredWalletData = useMemo(() => {
    const currentTabFilter = WALLET_FILTERS[currentTab];
    return currentTabFilter ? walletsData.filter(currentTabFilter) : [];
  }, [walletsData, currentTab]);

  return {
    activeTabIndices,
    activeTabLabels,
    filteredWalletData,
  };
}

export default useWalletTabs;
